import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';

/**
 * Series colors, in a fixed order checked for color-blind separation: this year/quotes, last year/orders, number of
 * quotes
 */
export const SERIES_COLORS = {
  first: '#2a78d6',
  second: '#eb6834',
  third: '#1baf7a',
  reference: '#52514e',
};

/** A section's data as it loads: each section loads, and fails, on its own */
export interface SectionState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface SectionCardProps<T> {
  title: string;
  subtitle?: string;
  state: SectionState<T>;
  onRetry: () => void;
  /** True when the data has nothing to show */
  isEmpty?: (data: T) => boolean;
  children: (data: T) => React.ReactNode;
}

/**
 * A dashboard section: a header that expands or collapses it, then its content once loaded, or a loading note, a
 * short error with Retry, or a note that there is no data. A reload keeps the previous content, dimmed.
 */
export function SectionCard<T>({
  title,
  subtitle,
  state,
  onRetry,
  isEmpty,
  children,
}: SectionCardProps<T>) {
  const { t } = useTranslation();
  const { data, loading, error } = state;
  const [open, setOpen] = useState(true);
  const Chevron = open ? ChevronDown : ChevronRight;
  const contentId = useId();

  return (
    <section className="rounded-lg border bg-white shadow-sm">
      <h2>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls={contentId}
          className="w-full flex items-start gap-2 p-4 sm:px-6 text-left rounded-lg hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          <Chevron
            className="w-5 h-5 mt-0.5 text-gray-500 flex-shrink-0"
            aria-hidden="true"
          />
          <span>
            <span className="block text-base font-semibold uppercase tracking-wide text-gray-900">
              {title}
            </span>
            {subtitle && (
              <span className="block text-sm font-normal text-gray-600 mt-0.5">
                {subtitle}
              </span>
            )}
          </span>
        </button>
      </h2>
      <div id={contentId} hidden={!open} className="px-4 pb-4 sm:px-6 sm:pb-6">
        {error ? (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800 flex-1">
              {t('quoteToCash.loadError', { section: title })}
            </p>
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-800 bg-white border border-red-300 rounded-md hover:bg-red-100"
            >
              <RefreshCw className="w-4 h-4" />
              {t('quoteToCash.retry')}
            </button>
          </div>
        ) : data === null ? (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">{t('common.loading')}</p>
          </div>
        ) : isEmpty?.(data) ? (
          <p className="text-sm text-gray-600 py-8 text-center">
            {t('quoteToCash.noData')}
          </p>
        ) : (
          <div className={loading ? 'opacity-50 transition-opacity' : ''}>
            {children(data)}
          </div>
        )}
      </div>
    </section>
  );
}

interface KpiTileProps {
  label: string;
  value: string;
  /** Lines under the value, e.g. a variance */
  detail?: React.ReactNode;
  /** Hover text explaining the figure */
  title?: string;
}

/** One headline figure */
export const KpiTile: React.FC<KpiTileProps> = ({
  label,
  value,
  detail,
  title,
}) => (
  <div
    className="rounded-lg border border-gray-200 bg-gray-50 p-4"
    title={title}
  >
    <p className="text-sm text-gray-600">{label}</p>
    <p className="mt-1 text-2xl font-semibold text-gray-900 tabular-nums">
      {value}
    </p>
    {detail && <div className="mt-1 text-sm text-gray-600">{detail}</div>}
  </div>
);

/** A variance in money and percent, green when ahead and red when behind */
export const Variance: React.FC<{
  amount: string;
  pct: string | null;
  value: number;
  suffix: string;
}> = ({ amount, pct, value, suffix }) => (
  <span>
    <span
      className={`font-medium tabular-nums ${value >= 0 ? 'text-green-700' : 'text-red-700'}`}
    >
      {amount}
      {pct !== null && ` / ${pct}`}
    </span>{' '}
    {suffix}
  </span>
);
